import type { HTMLAttributes } from "react";
import { cn } from "@drpcrd/utils";
import Linkify from "linkify-react";
import type { Opts } from "linkifyjs";

type ActivityTabMessageContentProps = HTMLAttributes<HTMLDivElement>;

const linkifyOptions: Opts = {
  target: "_blank",
  rel: "noopener noreferrer",
  className: "text-primary underline transition-opacity hover:opacity-80",
  validate: {
    url: (value) =>
      // Must contain at least one dot followed by 2+ letters (basic TLD check)
      /\.[a-z]{2,}/i.test(value),
  },
};

/**
 * Chat message bubble with styling variants
 */
const ActivityTabMessageContent = ({
  children,
  className,
  ...props
}: ActivityTabMessageContentProps) => (
  <div
    className={cn(
      "flex flex-col overflow-hidden rounded-lg px-2 py-1.5 text-sm",
      "group-data-[variant=primary]:bg-primary-50 group-data-[variant=primary]:text-primary-foreground group-data-[variant=primary]:rounded-tr-none",
      "group-data-[variant=secondary]:border-secondary group-data-[variant=secondary]:rounded-tl-none group-data-[variant=secondary]:border",
      "group-data-[variant=postit]:rounded-tr-none group-data-[variant=postit]:bg-[#FFF7DF]",
      className,
    )}
    {...props}
  >
    <Linkify options={linkifyOptions}>{children}</Linkify>
  </div>
);

/**
 * Chat message wrapper - alignment controlled by parent
 */
export type ActivityTabMessageProps = HTMLAttributes<HTMLDivElement> & {
  variant: "primary" | "secondary" | "postit";
};

export const ActivityTabMessage = ({
  className,
  variant,
  children,
  ...props
}: ActivityTabMessageProps) => (
  <div
    data-variant={variant}
    className={cn("group max-w-[90%]", className)}
    {...props}
  >
    <ActivityTabMessageContent>{children}</ActivityTabMessageContent>
  </div>
);
